/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { array, arrayOf, bool, func, number, object, oneOf, string, node } from 'prop-types';
import classNames from 'classnames/bind';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import PointCross from 'yoda-core-components/lib/components/PointCross/PointCross';
import Rating from 'yoda-core-components/lib/components/Rating/rating';
import S7Image from 'yoda-core-components/lib/components/S7Image/S7Image';
import {
    getURLParameterBykey,
    updateQueryStringParameter,
} from 'yoda-core-components/lib/helpers/Utils/Utils';
import {
    isDesktopWidth,
    isMobileWidth,
    desktopQuery,
} from 'yoda-core-components/lib/helpers/Utils/mediaQuery';
import get from 'lodash/get';
import ErrorBoundaryConnected from '../ErrorBoundary/ErrorBoundary';
import QuickAddToCartConnected from '../QuickAddTocart/QuickAddToCart';
import SaveForLaterConnected from '../SaveForLater/SaveForLater';
import SlickCarousel from '../SlickCarousel/SlickCarousel';
import * as styles from './ProductCard.css';
import { cardThemes, cardTypes, LAZY_LOAD_SCROLL_OFFSET } from './ProductCard.config';
import EcoRebates from './components/EcoRebates/EcoRebates';
import ExtendedSize from './components/ExtendedSize/ExtendedSize';
import ProductAvailabilityConnected from './components/ProductAvailability/ProductAvailability';
import ProductBadge from './components/ProductBadge/ProductBadge';
import ProductImage from './components/ProductImage/ProductImage';
import ProductPrice from './components/ProductPrice/ProductPrice';
import ProductTitle from './components/ProductTitle/ProductTitle';
import ProductVideo from './components/ProductVideo/ProductVideo';
import Swatches from './components/Swatches';
import {
    selectFeatureFlags,
    selectPreferencesNoImageFoundSrc,
    selectHostName,
} from '../../selectors/ContextSelector';
import SwatchesSkeleton from './components/Swatches/SwatchesSkeleton';
import { selectStoreAvailabilityFilter } from '../../selectors/filtersSelector';
import {
    setOnLoadBeaconStatus,
    setOnViewBeaconStatus,
    setOnClickBeaconStatus,
    setOnClickBeaconStatusForSwatch,
} from '../../actions/CriteoAction';

const cx = classNames.bind(styles);

const ProductCard = ({
    altImageUrl,
    badge,
    bottomBadge,
    cardCustomClass,
    cardTheme,
    cardType,
    customClass,
    defaultOffSet,
    ecoRebate,
    enableMiniPDP,
    enableProductTitlePrefix,
    imageUrl,
    index,
    isAddToCartClick,
    isCouponRecommendationsModal,
    isCriteoProduct,
    isEnsembledProduct,
    lazyLoadImages,
    noImageFoundSrcDefault,
    onAddToCartClick,
    onImageClick,
    onProductClick,
    ppId,
    pricingConfig,
    productUrl,
    shallowCard,
    showPaneLoader,
    showPricing,
    showSaveForlater,
    skuId,
    skuSwatch,
    title,
    upperBadge,
    videoId,
    enableGridViewInCertona,
    displayScene7PictureComponent,
    displayS7PictureComponentAltImg,
    fromHotDeals,
    availabilityStatus,
    galleryProductCard,
    triggerAnalytics,
    displayEach,
    eligibilityMessage,
    price,
    rating,
    reviewCount,
    lazyLoadSwatches,
    deviceType,
    deviceType: { isDesktop },
    children,
    setImageHeight,
    isproductList,
    primaryImageID,
    sponsoredProduct,
    OnBasketChangeBeacon,
    OnViewBeacon,
    OnClickBeacon,
    OnWishlistBeacon,
    showVerticalProductImage,
    enableVerticalImageLg,
}) => {
    const storeAvailabilityFilterData = useSelector(selectStoreAvailabilityFilter);

    const {
        lazyLoadNextGalleryCards = false,
        disableEnsembledImage = false,
        enableAddToCartForBOPIS = false,
        hideBottomBadge = false,
        hideTopLeftBadge = false,
        enableSaveForLater,
        enableSmoothScrollIOSGallerySearch = false,
        enableMobileSwatchTap = false,
        enableOptimizedPricing = false,
    } = useSelector(selectFeatureFlags);

    const hostname = useSelector(selectHostName);

    const dispatch = useDispatch();

    const { deviceType: { isBot = false } = {} } = ({ context }) => context;

    const noImageFoundSrc = useSelector(selectPreferencesNoImageFoundSrc);
    const criteoData = useSelector((state) => get(state, 'criteo', {}));
    const sponsoredProductsRenderCount = useSelector((state) =>
        get(state, 'productList.sponsoredRenderedList', 0)
    );

    const productCardRef = useRef();

    const [imageData, setImageData] = useState({
        imageError: false,
        imageSrc: '',
        swatchImageSrc: '',
        swatchSkuId: null,
        activeSwatchImageId: '',
        altImageUrl,
        swatchClicked: false,
    });

    const [defaultSwatchData, setDefaultSwatchData] = useState(null);
    const [playVideo, setPlayVideo] = useState(false);
    const [enableProductSliding, setEnableProductSliding] = useState(() => !isDesktopWidth());
    const [imageLoad, setImageLoad] = useState(false);
    const [carouselSlideDefault, setCarouselActive] = useState(false);
    const [timer, setTimer] = useState(null);

    const {
        sponsoredProduct: { data = {} } = {},
        onLoadBeaconCalled = false,
        onViewBeaconCalledPPIDs = [],
        onClickBeaconCalledPPIDs = [],
        onClickBeaconCalledSwatchPPIDs = [],
        criteoGroupBeacons = {},
    } = criteoData;

    /* istanbul ignore next */
    const handleResize = (event) => {
        setEnableProductSliding(!event?.matches);
    };

    const setActiveDefaultCarousel = () => {
        setCarouselActive(false);
    };

    useEffect(() => {
        const onLoadBeaconFromSession = sessionStorage.getItem('onLoadBeaconCalled');
        const onLoadCalled = onLoadBeaconCalled || onLoadBeaconFromSession === 'true';
        const onLoadBeaconCall = criteoGroupBeacons?.OnLoadBeacon;

        if (!onLoadCalled) {
            let products = '';
            let status = '';

            if (data?.allProducts?.length > 0) {
                data?.OOSProducts?.forEach((product) => {
                    products += `${product?.ProductId}|`;
                    status += `${303}|`;
                });

                data?.qualifiedProducts?.slice(sponsoredProductsRenderCount)?.forEach((product) => {
                    products += `${product?.ProductId}|`;
                    status += `${103}|`;
                });

                data?.ProductsNotFound?.forEach((product) => {
                    products += `${product?.ProductId}|`;
                    status += `${301}|`;
                });

                data?.allProducts?.forEach((product) => {
                    if (
                        product?.ppId === undefined &&
                        product?.skuId === undefined &&
                        product?.productUrl === undefined
                    ) {
                        products += `${product?.ProductId}|`;
                        status += `${103}|`;
                    }
                });

                products = products?.slice(0, -1)?.replace(/"/g, ''); // Remove the last pipe
                status = status?.slice(0, -1); // Remove the last pipe
                navigator.sendBeacon(`https:${onLoadBeaconCall}&ex=${products}&st=${status}`);
                dispatch(setOnLoadBeaconStatus());
            }
        }
    }, [onLoadBeaconCalled, data]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // Stop observing the current element
                        observer.unobserve(entry.target);
                        // Set a timer when the product card comes into view
                        /* istanbul ignore next */
                        const timeout = setTimeout(() => {
                            if (sponsoredProduct) {
                                // Fire the OnViewBeacon function if it's a sponsored product
                                const onLoadViewBeaconCall = criteoGroupBeacons?.OnViewBeacon;
                                const onLoadViewBeaconCalled = sessionStorage.getItem(
                                    'onLoadViewBeaconFromSession'
                                );
                                if (
                                    onLoadViewBeaconCall &&
                                    sponsoredProductsRenderCount > 0 &&
                                    !(onLoadViewBeaconCalled === 'true')
                                ) {
                                    navigator.sendBeacon(`https:${onLoadViewBeaconCall}`);
                                    sessionStorage.setItem('onLoadViewBeaconFromSession', true);
                                }

                                if (
                                    OnViewBeacon &&
                                    sponsoredProductsRenderCount > 0 &&
                                    !onViewBeaconCalledPPIDs?.includes(entry.target.dataset.skuId)
                                ) {
                                    navigator.sendBeacon(`https:${OnViewBeacon}`);
                                    dispatch(setOnViewBeaconStatus(entry.target.dataset.skuId));
                                }
                            }
                        }, 1000); // 1 second delay
                        setTimer(timeout); // Assuming setTimer updates state to manage cleanup
                    }
                });
            },
            { threshold: 0.5 } // Trigger when 50% of the element is in view
        );

        // Assuming productCardsRef is an array of refs to each product card
        if (productCardRef.current && sponsoredProduct) {
            observer.observe(productCardRef.current);
        }

        return () => {
            /* istanbul ignore next */
            if (productCardRef.current) {
                observer.unobserve(productCardRef.current);
            }
            clearTimeout(timer); // Cleanup the timer on component unmount
        };
    }, [productCardRef, sponsoredProduct, OnViewBeacon]); // Dependencies

    useEffect(() => {
        const query = window.matchMedia(desktopQuery);
        /* istanbul ignore next */
        query.onchange = (e) => handleResize(e);
    }, []);

    const lazyLoadGalleryCardsNumber = 8;
    const scene7smallScreenOffset = showVerticalProductImage && enableVerticalImageLg ? 248 : 170;
    const scene7BigScreenOffset = showVerticalProductImage && enableVerticalImageLg ? 400 : 350;
    let scene7smallScreenOffsetURL = '';
    let scene7BigScreenOffsetURL = '';
    let scene7smallScreenOffsetAltURL = '';
    let scene7BigScreenOffsetAltURL = '';

    const lazyLoadGalleryCardsScrollOffset =
        defaultOffSet > LAZY_LOAD_SCROLL_OFFSET[lazyLoadGalleryCardsNumber]
            ? defaultOffSet
            : LAZY_LOAD_SCROLL_OFFSET[lazyLoadGalleryCardsNumber];

    const pointCrossOffsetProps = lazyLoadNextGalleryCards
        ? { offset: lazyLoadGalleryCardsScrollOffset }
        : {};

    const onImageLoad = () => {
        if (!imageLoad) {
            setImageLoad(true);
        }
    };

    /* istanbul ignore next */
    const handleSwatchDesktopEvent = useCallback(
        (event, currentSwatch) => {
            event.preventDefault();
            const getScene7ImageUrl = (imageId) =>
                (imageId &&
                    `//jcpenney.scene7.com/is/image/JCPenney/${imageId}?wid=350&hei=350&op_usm=.4,.8,0,0&resmode=sharp2`) ||
                '';

            if (currentSwatch?.skuId) {
                // TOFP2P-2033: stop video playing when swatch is selected
                setPlayVideo(false);

                const {
                    colorizedImageId = '',
                    skuAltImages: [{ imgId = '' } = {}] = [],
                } = currentSwatch;
                const altImgUrl = getScene7ImageUrl(imgId);
                const imgUrl = getScene7ImageUrl(colorizedImageId);

                const newImageData = {
                    imageError: false,
                    imageSrc: imgUrl,
                    swatchImageSrc: imgUrl,
                    swatchSkuId: currentSwatch?.skuId,
                    swatchAltImgUrl: altImgUrl,
                };

                if (!disableEnsembledImage)
                    newImageData.altImageUrl = isEnsembledProduct ? altImgUrl || imgUrl : altImgUrl;

                if (event?.type === 'click') {
                    /* istanbul ignore if */
                    // Comenting for this Story TOFP2P-4816
                    // if (
                    //     OnClickBeacon &&
                    //     !onClickBeaconCalledSwatchPPIDs?.includes(currentSwatch?.skuId)
                    // ) {
                    //     navigator.sendBeacon(`https:${OnClickBeacon}`);
                    //     dispatch(setOnClickBeaconStatusForSwatch(currentSwatch?.skuId));
                    // }
                    newImageData.activeSwatchImageId = currentSwatch?.swatchImageId;
                    newImageData.swatchClicked = true;
                    setDefaultSwatchData({ ...newImageData });
                    if (enableMobileSwatchTap && !isDesktopWidth()) {
                        // setEnableProductSliding(colorizedImageId === primaryImageID);
                        setCarouselActive(true);
                    }
                }

                const shouldUpdateImageData =
                    !enableMobileSwatchTap || isDesktopWidth() || event?.type !== 'mouseover';

                if (shouldUpdateImageData) {
                    setImageData((prevImageData) => ({
                        ...prevImageData,
                        ...newImageData,
                    }));
                }
            }

            if (event?.type === 'mouseleave' && !imageData?.swatchClicked) {
                setImageData((prevImageData) => {
                    return {
                        ...prevImageData,
                        imageSrc: '',
                        swatchImageSrc: '',
                        swatchSkuId: null,
                        activeSwatchImageId: '',
                        altImageUrl,
                    };
                });
            }
        },
        [imageData]
    );

    /* istanbul ignore next */
    const setGalleryScrollInLocalStorage = useCallback(() => {
        const topOfElement = !__SERVER__ ? window.pageYOffset : 1000;
        LocalStorage.setData('galleryScrollTo', ppId);
        LocalStorage.setData('galleryScrollToOffSet', topOfElement);
    }, [ppId]);

    /* istanbul ignore next */
    const getFacetSelectedValue = (url) => {
        const facetSelected = getURLParameterBykey('facetSelected', url);

        if (!facetSelected || facetSelected?.length === 0) {
            return 'color';
        }
        const hasColorFacet = facetSelected?.split(',')?.includes('color');
        return hasColorFacet ? facetSelected : `${facetSelected},color`;
    };

    // for Mobile and Tablet redirect to PDP
    /* istanbul ignore next */
    // eslint-disable-next-line no-shadow
    const handleSwatchMobileEvent = useCallback(
        ({ skuId }) => {
            if (skuId && productUrl) {
                const updatedProductUrl = updateQueryStringParameter(
                    productUrl,
                    'selectedSKUId',
                    skuId
                );

                LocalStorage.setData('fromGallery', { scrollTo: 'swatch' });
                setGalleryScrollInLocalStorage();
                /* istanbul ignore if */
                if (OnClickBeacon && !onClickBeaconCalledSwatchPPIDs?.includes(skuId)) {
                    navigator.sendBeacon(`https:${OnClickBeacon}`);
                    dispatch(setOnClickBeaconStatusForSwatch(skuId));
                }
                window.location.assign(
                    updateQueryStringParameter(
                        updatedProductUrl,
                        'facetSelected',
                        getFacetSelectedValue(updatedProductUrl)
                    )
                );
            }
        },
        [productUrl]
    );

    const handleVideoChange = (newValue) => {
        setPlayVideo(newValue);
        /* istanbul ignore if */
        if (OnClickBeacon && !onClickBeaconCalledPPIDs?.includes(skuId)) {
            navigator.sendBeacon(`https:${OnClickBeacon}`);
            dispatch(setOnClickBeaconStatus(skuId));
        }
    };

    const getProductVideo = (additionalProps) => {
        return videoId ? (
            <ProductVideo
                videoId={videoId}
                index={index}
                galleryProductCard={galleryProductCard}
                triggerAnalytics={triggerAnalytics}
                playVideo={playVideo}
                setParentState={handleVideoChange}
                {...additionalProps}
            />
        ) : null;
    };
    const getExtendedSize = (additionalProps) => {
        return bottomBadge?.text ? (
            <ExtendedSize bottomBadge={bottomBadge} cardTheme={cardTheme} {...additionalProps} />
        ) : null;
    };

    const getProductBadge = useCallback(() => {
        const activeBadge = badge || upperBadge;
        return activeBadge?.text ? (
            <ProductBadge badge={activeBadge} playVideo={playVideo} />
        ) : null;
    }, [badge, upperBadge, playVideo]);

    /**
     * display default image for ensembled products
     * when user hovers out of img/alt-image/swatches
     */
    /* istanbul ignore next */
    const resetSelectionForEnsembleHover = isproductList
        ? useCallback(() => {
              if (isEnsembledProduct) {
                  const newImageData = {
                      swatchImageSrc: '',
                      imageSrc: '',
                      imageError: false,
                      swatchSkuId: '',
                      activeSwatchImageId: '',
                  };
                  setImageData(defaultSwatchData || newImageData);
              }
          }, [isEnsembledProduct])
        : () => {};

    const constructProductImagesAndVideos = () => {
        const productImagesAndVideos = [];
        const { swatchImageSrc = '', swatchAltImgUrl = '' } = imageData;

        const formatImageUrls = (url, offset, urlFormatCheck) => {
            return `${url}${urlFormatCheck ? '&' : '?'}wid=${offset}&hei=${offset}&op_sharpen=1`;
        };
        const isdisplayUrlFormat = (url) => url.indexOf('?') > 0;
        const matchImage = (url) => /scene7.com(.+)/g.test(url);

        const productImageUrl = swatchImageSrc && enableMobileSwatchTap ? swatchImageSrc : imageUrl;

        if (productImageUrl) {
            let displayUrl = productImageUrl;
            if (matchImage(productImageUrl) && displayScene7PictureComponent) {
                scene7smallScreenOffsetURL = formatImageUrls(
                    productImageUrl,
                    scene7smallScreenOffset,
                    isdisplayUrlFormat
                );
                scene7BigScreenOffsetURL = formatImageUrls(
                    productImageUrl,
                    scene7BigScreenOffset,
                    isdisplayUrlFormat
                );
                if (displayS7PictureComponentAltImg) {
                    displayUrl = isMobileWidth()
                        ? scene7smallScreenOffsetURL
                        : scene7BigScreenOffsetURL;
                }
            } else if (matchImage(productImageUrl)) {
                displayUrl = `${displayUrl}${
                    isdisplayUrlFormat(productImageUrl) ? '&' : '?'
                }wid=170&hei=170&op_sharpen=1`;
            }
            const imageobj = {
                altText: title,
                displayText: title,
                key: displayUrl,
                type: 'PRIMARY',
                url: displayUrl,
            };

            productImagesAndVideos.push(imageobj);
        }

        const productImageAltUrl =
            swatchAltImgUrl && enableMobileSwatchTap ? swatchAltImgUrl : altImageUrl;

        if (productImageAltUrl) {
            let displayAltImgUrl = productImageAltUrl;
            /* istanbul ignore next */
            if (matchImage(productImageAltUrl) && displayS7PictureComponentAltImg) {
                scene7smallScreenOffsetAltURL = formatImageUrls(
                    productImageAltUrl,
                    scene7smallScreenOffset,
                    displayAltImgUrl
                );
                scene7BigScreenOffsetAltURL = formatImageUrls(
                    productImageAltUrl,
                    scene7BigScreenOffset,
                    displayAltImgUrl
                );
                displayAltImgUrl = isMobileWidth()
                    ? scene7smallScreenOffsetAltURL
                    : scene7BigScreenOffsetAltURL;
            } else if (matchImage(productImageAltUrl)) {
                displayAltImgUrl = `${displayAltImgUrl}${
                    isdisplayUrlFormat(productImageAltUrl) ? '&' : '?'
                }wid=170&hei=170&op_sharpen=1`;
            }
            const altImageobj = {
                altText: title,
                key: displayAltImgUrl,
                swatchUrl: displayAltImgUrl,
                type: 'ALTERNATE',
                url: displayAltImgUrl,
            };

            productImagesAndVideos.push(altImageobj);
        }

        return productImagesAndVideos;
    };

    const renderProductImage = () => {
        const swatchAltImage = imageData?.altImageUrl;
        const { imageSrc, swatchImageSrc, swatchSkuId } = imageData;

        const propsClone = {
            cardTheme,
            imageUrl,
            lazyLoadImages,
            onProductClick,
            title,
            productUrl,
        };

        /* istanbul ignore next */
        if (swatchSkuId) {
            const productUrlUpdated = updateQueryStringParameter(
                productUrl,
                'selectedSKUId',
                swatchSkuId
            );

            const productUpdateFacetUrl = updateQueryStringParameter(
                productUrlUpdated,
                'facetSelected',
                getFacetSelectedValue(productUrlUpdated)
            );

            propsClone.productUrl = productUpdateFacetUrl;
        }
        const altImgUrl = swatchAltImage;
        const enableProductImage = !enableProductSliding || cardTheme !== cardThemes?.GALLERY;

        const productImageProps = {
            ...propsClone,
            altImageUrl: altImgUrl,
            imageSrc,
            pointCrossOffsetProps,
            swatchImageSrc,
            onLoad: onImageLoad(),
            sponsoredProduct,
            showVerticalProductImage,
        };

        return enableProductImage && <ProductImage {...productImageProps} />;
    };

    const renderProductSliding = () => {
        // all below changes are for SlickCarousel
        const productImagesAndVideos = galleryProductCard && constructProductImagesAndVideos();
        const productImageAndVideosLength = productImagesAndVideos?.length;

        // clean up styles
        const carouselTheme = {
            carouselSelected: cx('selected'),
            carouselBullets: cx('carouselBullets'),
            bullet: cx('bullet'),
            slideClass:
                showVerticalProductImage && !lazyLoadImages
                    ? cx('carouselSlideVerticalImage')
                    : cx('carouselSlidecx'),
            bulletContainer: cx('bulletContainer'),
            carouselContainer: galleryProductCard ? cx('carouselContainerGallery') : '',
        };
        const key = `${productImageAndVideosLength}-s`;
        const displayProductSliding = enableProductSliding && productImageAndVideosLength > 0; // as product card will be common component added to make sure it will load only for gallery not for RR zone
        const carouselThemeGallery = {
            bulletContainer: `${dt([
                'absolute',
                'bottom-1',
                'w-full',
                'bg-transparent',
                'align-top',
            ])}`,
            bullet: `${dt(['align-top', 'ml-2', 'mr-0', 'border-0'])}`,
        };

        const productImageListRenderer = (dataItem, idx) => {
            const { url = '', altText = '' } = dataItem;
            const noImageSrc = noImageFoundSrc || noImageFoundSrcDefault;

            let productUpdatedUrl = productUrl;
            const { swatchSkuId } = imageData;

            /* istanbul ignore next */
            if (swatchSkuId) {
                productUpdatedUrl = updateQueryStringParameter(
                    productUrl,
                    'selectedSKUId',
                    swatchSkuId
                );
                productUpdatedUrl = updateQueryStringParameter(
                    productUpdatedUrl,
                    'facetSelected',
                    getFacetSelectedValue(productUpdatedUrl)
                );
            }

            /* istanbul ignore next */
            const pictureimgUrl = isMobileWidth()
                ? scene7smallScreenOffsetURL
                : scene7BigScreenOffsetURL;
            const image = (
                <a
                    href={productUpdatedUrl}
                    onClick={(e) => {
                        onImageClick(e, 'image');
                    }}
                >
                    <ErrorBoundaryConnected>
                        {displayScene7PictureComponent ? (
                            // eslint-disable-next-line react/no-unknown-property
                            <picture automationId="at-responsive-component-image">
                                {/* small/mobile breakpoint */}
                                <source
                                    media="(max-width: 767px)"
                                    srcSet={
                                        displayS7PictureComponentAltImg
                                            ? url
                                            : scene7smallScreenOffsetURL
                                    }
                                />
                                {/* large/desktop breakpoint */}
                                <S7Image
                                    src={displayS7PictureComponentAltImg ? url : pictureimgUrl}
                                    alt={altText}
                                    automationId={`slick-1-s7image-${idx}`}
                                    imageErrorSrc={noImageSrc}
                                    renderFromPictureComponent
                                    onLoad={onImageLoad()}
                                />
                            </picture>
                        ) : (
                            <S7Image
                                src={url}
                                alt={altText}
                                automationId={`slick-1-s7image-${idx}`}
                                imageErrorSrc={noImageSrc}
                                onLoad={onImageLoad()}
                            />
                        )}
                    </ErrorBoundaryConnected>
                </a>
            );

            return lazyLoadImages ? (
                <PointCross
                    lazyLoadComponent
                    fromGallery
                    wrapperClass={showVerticalProductImage && cx('carouselSlideVerticalImage')}
                    {...pointCrossOffsetProps}
                    loader={
                        galleryProductCard ? (
                            <div
                                className={dt([
                                    `${showVerticalProductImage && 'sm:pt-[140%]'}`,
                                    `${
                                        showVerticalProductImage
                                            ? 'sm:min-h-[248px]'
                                            : 'sm:min-h-[150px]'
                                    }`,
                                    'md:min-h-[235px]',
                                    'lg:min-h-[300px]',
                                ])}
                            />
                        ) : null
                    }
                >
                    {image}
                </PointCross>
            ) : (
                image
            );
        };

        return displayProductSliding ? (
            <ErrorBoundaryConnected>
                <SlickCarousel
                    isbotWrapperClass={isBot && galleryProductCard ? 'isbotWrapperClass' : ''}
                    carouselData={productImagesAndVideos}
                    carouselItemRenderer={productImageListRenderer}
                    themeConfig={carouselTheme}
                    automationId="data-slick-carousel"
                    rewindOnChange
                    autoSize
                    lazyLoad
                    displayArrows={false}
                    repeat={false}
                    carouselBulletTheme={carouselThemeGallery}
                    key={key}
                    carouselSlideDefault={carouselSlideDefault}
                    setActiveDefaultCarousel={() => setActiveDefaultCarousel()}
                    enableMobileSwatchTap={enableMobileSwatchTap && !isDesktopWidth()}
                    sponsoredProduct={sponsoredProduct}
                    OnClickBeacon={OnClickBeacon}
                    ppId={ppId}
                    skuId={skuId}
                />
            </ErrorBoundaryConnected>
        ) : null;
    };

    const renderPricing = useMemo(() => {
        return showPricing ? (
            <ProductPrice
                {...{
                    cardTheme,
                    deviceType,
                    displayEach,
                    eligibilityMessage,
                    ppId,
                    price,
                    pricingConfig,
                    fromHotDeals,
                    enableOptimizedPricing,
                }}
            />
        ) : null;
    }, [
        showPricing,
        cardTheme,
        deviceType,
        displayEach,
        eligibilityMessage,
        ppId,
        price,
        pricingConfig,
        fromHotDeals,
    ]);

    const renderRating = useMemo(() => {
        const config = {
            anchor: {
                href: productUrl,
                onClick: (event) => {
                    onProductClick(event, 'reviews');
                },
                ...(sponsoredProduct && { rel: 'sponsored' }),
            },
            rating: {
                automationId: 'productCard-automation-rating',
                averageRating: rating,
                className: cardTheme,
                ratingIconSize: 'small',
                totalRatings:
                    typeof reviewCount === 'string' ? parseInt(reviewCount, 10) : reviewCount,
                wrapperClassName: dt(['mt-1', 'sm:!leading-6']),
                showCount: true,
            },
        };

        return productUrl ? (
            <a {...config?.anchor}>
                <Rating {...config?.rating} />
            </a>
        ) : (
            <Rating {...config?.rating} />
        );
    }, [productUrl, rating, cardTheme, reviewCount]);

    const renderCard = () => {
        const { text = '' } = upperBadge;
        const { activeSwatchImageId, swatchSkuId } = imageData;
        const propsClone = { productUrl };
        const productAnalyticsData = {
            productName: title || '',
            productImageSrc: imageUrl ? `https:${imageUrl}` : '',
            productLink:
                hostname && productUrl ? `https://${hostname}${productUrl.split('&')[0]}` : '',
        };
        const titlePrefix =
            enableProductTitlePrefix && title && text?.toLowerCase().indexOf('new') > -1
                ? 'New!'
                : '';

        if (swatchSkuId)
            propsClone.productUrl = updateQueryStringParameter(
                productUrl,
                'selectedSKUId',
                swatchSkuId
            );

        // Adding a check to stop sending skuId to quickAddToCart as there was some issue with pre-selection of options in mini-PDP.
        const quickAddToCartSkuId = galleryProductCard ? '' : skuId;
        const saveForLaterDisplay = isproductList
            ? useMemo(() => {
                  if (showSaveForlater && enableSaveForLater) {
                      const isFirstProduct = index === 1;
                      return (
                          <div className={cx('saveForLaterContainer')}>
                              <SaveForLaterConnected
                                  cardType={cardType}
                                  isFirstProduct={isFirstProduct}
                                  ppId={ppId}
                                  skuId={skuId}
                                  OnWishlistBeacon={OnWishlistBeacon}
                                  sponsoredProduct={sponsoredProduct}
                                  productAnalyticsData={productAnalyticsData}
                              />
                          </div>
                      );
                  }
                  return null;
              }, [showSaveForlater, cardType, ppId, skuId])
            : null;

        const renderProductTitle = useMemo(() => {
            return (
                <ProductTitle
                    prefix={titlePrefix}
                    onProductClick={onProductClick}
                    title={title}
                    productUrl={propsClone?.productUrl}
                    sponsoredProduct={sponsoredProduct}
                />
            );
        }, [title, propsClone?.productUrl]);

        const renderSkeletonLoader = () => (
            <div
                className={`${dt([
                    'absolute',
                    'top-0',
                    'h-full',
                    'w-full',
                    'bg-light-white',
                    'overflow-hidden',
                ])} ${cx('imageLoading')}`}
            />
        );
        const { disableFPACAPI } = pricingConfig;
        const enableNewPriceFormat = disableFPACAPI === false;

        const isStoreAvailable = storeAvailabilityFilterData?.length
            ? !!(
                  storeAvailabilityFilterData?.length &&
                  storeAvailabilityFilterData[0]?.facetValueList?.length
              )
            : false;

        const facetValueList = isStoreAvailable
            ? storeAvailabilityFilterData[0].facetValueList
            : [];

        /* returns if SDPU filter is enabled in gallery */
        let { selected } =
            facetValueList.find(
                (facetConfig) => facetConfig?.facetValId === 832 && !facetConfig?.greyOut
            ) || {};

        /* should always return true is flag is enabled */
        if (enableAddToCartForBOPIS) {
            selected = false;
        }

        const renderQuickAddToCart = isproductList
            ? useMemo(() => {
                  return (
                      <QuickAddToCartConnected
                          ppId={ppId}
                          productskuId={skuId}
                          skuId={quickAddToCartSkuId}
                          automationId="QuickAddToCart"
                          themeConfig={{
                              buttonCustomClass: cx('quickAddToCart', 'iconButtonCart'),
                              wrapperClass: cx('quickAddToCartWrapper', 'iconCartWrapper'),
                          }}
                          location={cardTheme}
                          url={
                              swatchSkuId
                                  ? updateQueryStringParameter(
                                        productUrl,
                                        'selectedSKUId',
                                        swatchSkuId
                                    )
                                  : productUrl
                          }
                          iconStyle
                          enableMiniPDP={enableMiniPDP && !selected}
                          onAddToCartClick={onAddToCartClick}
                          isAddToCartClick={isAddToCartClick}
                          gallerySmoothScroll={enableSmoothScrollIOSGallerySearch}
                          OnBasketChangeBeacon={OnBasketChangeBeacon}
                      />
                  );
              }, [selected])
            : null;

        const renderSwatches = isproductList
            ? useCallback(() => {
                  if (skuSwatch?.length) {
                      const swatchComponent = () => (
                          <Swatches
                              {...{
                                  activeSwatchImageId,
                                  handleSwatchDesktopEvent,
                                  handleSwatchMobileEvent,
                                  isEnsembledProduct,
                                  productCardRef,
                                  productUrl,
                                  setGalleryScrollInLocalStorage,
                                  skuSwatch,
                                  index,
                                  desktopWidth: !enableProductSliding,
                                  OnClickBeacon,
                                  onClickBeaconCalledPPIDs,
                                  skuId,
                              }}
                          />
                      );
                      return (
                          // TODO: MNPDPYODA-12118 removed lazyload - this needs to be refactored
                          lazyLoadSwatches ? (
                              <PointCross
                                  lazyLoadComponent
                                  {...pointCrossOffsetProps}
                                  loader={galleryProductCard ? <SwatchesSkeleton /> : null}
                              >
                                  {swatchComponent()}
                              </PointCross>
                          ) : (
                              swatchComponent()
                          )
                      );
                  }

                  return null;
              }, [skuSwatch, lazyLoadSwatches, activeSwatchImageId, enableProductSliding])
            : () => null;

        return (
            <div
                className={cx('container', cardType, {
                    miniPDPEnabled: enableMiniPDP,
                })}
            >
                <div
                    ref={(elm) => {
                        setImageHeight && setImageHeight(elm);
                    }}
                    className={cx('imgBlock', {
                        catridgeImgBlock: enableNewPriceFormat,
                        galleryVideoImgBlock: galleryProductCard && videoId,
                    })}
                >
                    {/* With the enablePrice Feature Flag 'catridgeImgBlock' have to append */}

                    {getProductVideo({ renderVideo: true })}
                    {!hideTopLeftBadge && getProductBadge()}
                    <div
                        className={cx('imageDetails', playVideo && 'hideImage', {
                            gridimageDetails: enableGridViewInCertona,
                        })}
                    >
                        {renderProductSliding()}
                        {renderProductImage()}
                        {getProductVideo()}
                        {!hideBottomBadge && getExtendedSize()}
                        {renderQuickAddToCart}
                    </div>
                    {saveForLaterDisplay}
                    {galleryProductCard && !imageLoad && renderSkeletonLoader()}
                </div>
                {/* eslint-disable-next-line no-nested-ternary */}
                {renderSwatches()}
                <div
                    className={`${dt(['pt-0', 'pr-0.5', 'mt-2', 'block', 'relative'])} ${cx({
                        newPriceDetailsWrapper: enableNewPriceFormat,
                    })}`}
                    onFocus={resetSelectionForEnsembleHover}
                    onMouseOver={resetSelectionForEnsembleHover}
                    data-component-name="newPriceDetailsWrapper"
                >
                    {renderProductTitle}
                    {!hideBottomBadge ? getExtendedSize({ inDescription: true }) : null}
                    {renderPricing}
                    {ecoRebate ? <EcoRebates skuId={skuId} /> : null}
                    {isCriteoProduct ? (
                        <div className={cx('sponsored')}>Sponsored Product</div>
                    ) : null}
                    {renderRating}
                    {sponsoredProduct ? (
                        <div
                            className={`${dt([
                                'pt-1.5',
                                'font-normal',
                                'font-open-sans',
                                'text-gray-60',
                                'text-xsmall',
                            ])}`}
                        >
                            Sponsored
                        </div>
                    ) : null}
                    <ProductAvailabilityConnected
                        deviceType={deviceType}
                        cardType={cardType}
                        availabilityStatus={availabilityStatus}
                    />
                    {children}
                </div>
            </div>
        );
    };

    return (
        <div
            className={cx('productCard', customClass, cardTheme, {
                couponRecommendations: isCouponRecommendationsModal,
            })}
            ref={productCardRef}
            data-ppId={ppId}
            data-skuId={skuId}
        >
            <div
                className={cx('card', 'cardWrapper', cardCustomClass, {
                    cardMinWidth: isDesktop,
                })}
                data-automation-id="productCard"
            >
                {shallowCard && showPaneLoader ? (
                    <div className={cx('col')}>
                        <div className={cx('row')}>
                            <div className={cx('imageTileLoader')} />
                        </div>
                        <div className={cx('row')}>
                            <div className={cx('loader', 'descLoader')} />
                            <div className={cx('loader', 'descLoader')} />
                            <div className={cx('loader', 'descLoader')} />
                        </div>
                    </div>
                ) : (
                    renderCard()
                )}
            </div>
        </div>
    );
};

ProductCard.defaultProps = {
    altImageUrl: '',
    badge: null,
    bottomBadge: {},
    cardCustomClass: '',
    cardTheme: cardThemes.GALLERY,
    cardType: cardTypes.GRID,
    customClass: '',
    defaultOffSet: 500,
    ecoRebate: false,
    enableMiniPDP: false,
    enableProductTitlePrefix: false,
    imageUrl: '',
    index: 0,
    isAddToCartClick: false,
    isCouponRecommendationsModal: false,
    isCriteoProduct: false,
    isEnsembledProduct: false,
    lazyLoadImages: false,
    noImageFoundSrcDefault: 'https://m.jcpenney.com/b/assets/img/noimage.jpeg',
    onAddToCartClick: () => {},
    onImageClick: () => {},
    onProductClick: () => {},
    ppId: '',
    pricingConfig: {},
    productUrl: '',
    shallowCard: false,
    showPaneLoader: false,
    showPricing: true,
    showSaveForlater: false,
    skuId: '',
    skuSwatch: [],
    title: '',
    upperBadge: {},
    videoId: '',
    enableGridViewInCertona: false,
    displayScene7PictureComponent: false,
    displayS7PictureComponentAltImg: true,
    fromHotDeals: false,
    availabilityStatus: [],
    galleryProductCard: false,
    triggerAnalytics: () => {},
    displayEach: false,
    eligibilityMessage: '',
    price: null,
    rating: 0,
    reviewCount: 0,
    lazyLoadSwatches: false,
    deviceType: { isDesktop: true },
    setImageHeight: () => {},
    sponsoredProduct: false,
};

ProductCard.propTypes = {
    altImageUrl: string,
    badge: object,
    bottomBadge: object,
    cardCustomClass: string,
    cardTheme: oneOf([cardThemes.CARTRIDGE, cardThemes.GALLERY, cardThemes.ONE_RECORD_BANNER]),
    cardType: oneOf([cardTypes.GRID, cardTypes.LIST]),
    customClass: string,
    defaultOffSet: number,
    ecoRebate: bool,
    enableMiniPDP: bool,
    enableProductTitlePrefix: bool,
    imageUrl: string,
    index: number,
    isAddToCartClick: bool,
    isCouponRecommendationsModal: bool,
    isCriteoProduct: bool,
    isEnsembledProduct: bool,
    lazyLoadImages: bool,
    noImageFoundSrcDefault: string,
    onAddToCartClick: func,
    onImageClick: func,
    onProductClick: func,
    ppId: string,
    pricingConfig: object,
    productUrl: string,
    shallowCard: bool,
    showPaneLoader: bool,
    showPricing: bool,
    showSaveForlater: bool,
    skuId: string,
    skuSwatch: array,
    title: string,
    upperBadge: object,
    videoId: string,
    enableGridViewInCertona: bool,
    displayScene7PictureComponent: bool,
    displayS7PictureComponentAltImg: bool,
    fromHotDeals: bool,
    availabilityStatus: arrayOf(object),
    galleryProductCard: bool,
    triggerAnalytics: func,
    displayEach: bool,
    eligibilityMessage: string,
    price: object,
    rating: number,
    reviewCount: number,
    lazyLoadSwatches: bool,
    deviceType: object,
    children: node.isRequired,
    setImageHeight: func,
    sponsoredProduct: bool,
};

export default React.memo(ProductCard);
